import React, { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { startSubscription } from "../../slices/subscriptionSlice";
import type { SubscriptionPackage, PackageBilling, PackagePermissions } from "../../types/subscription";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import PaystackButton from "../PaystackButton";
import { XCircle } from "lucide-react";

interface Props {
  open: boolean;
  onClose: () => void;
  pkg: SubscriptionPackage;
  isFirstTimer: boolean; // true => freeTrial possible
  onSubscriptionSuccess?: () => void;
}


const formatLimit = (val: string | number | null) => ((val === null || val === 0) ? "Unlimited" : String(val));



const SubscriptionModal: React.FC<Props> = ({ open, onClose, pkg, isFirstTimer, onSubscriptionSuccess }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector((s) => s.auth.user);


  // default choose first billing option
  const billingOptions = pkg.PackageBilling ?? [];
  const [selectedBillingId, setSelectedBillingId] = useState<string | null>(
    billingOptions?.[0]?.id ?? null
  );

  const [autoRenew, setAutoRenew] = useState(true);
  const [freeTrial, setFreeTrial] = useState(false);
  const [amount, setAmount] = useState<string>(billingOptions?.[0]?.bill as string);
  const [submitting, setSubmitting] = useState(false);

  // optional present nicer label for billing cycle
  const billingLabel = (b: PackageBilling) =>
    `${b.monthlyPeriod ?? b.paymentInterval ?? "Billing"} — ${b.bill ? `GH₵${b.bill}` : "Contact"}`;

  // details
  const details: PackagePermissions | undefined = pkg.PackageDetails?.[0];

  const onConfirm = async () => {
    if (!selectedBillingId) {
      toast.error("Please select a billing option");
      return;
    }
    setSubmitting(true);
    try {
      const billing = billingOptions.find((b) => b.id === selectedBillingId);
      const billing_cycle = billing?.monthlyPeriod ?? "monthly";

      await dispatch(
        startSubscription({
          selectedPackage: pkg,
          payload: {
            package_id: pkg.id,
            billing_id: selectedBillingId,
            billing_cycle,
            auto_renew: autoRenew,
            freeTrial: freeTrial,
            amount: freeTrial ? 0 : amount
          }
        })
      ).unwrap();

      if (onSubscriptionSuccess) {
        onSubscriptionSuccess();
      } else {
        navigate("/");
      }
      toast.success("Subscription started successfully");

    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || "Failed to start subscription");
    } finally {
      setSubmitting(false);
    }
  };

  console.log("Package: ", pkg);

  if (!open) return null;

  return (
    <div className="modal">
      <div className="modal-body">
        <div className="p-6">
          <div className="flex items-start justify-between">
            <h3 className="text-xl font-semibold">{pkg.name} — Choose Billing</h3>
            {/* <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 ml-4"
              aria-label="close"
            >
              ✕
            </button> */}
            <XCircle
              className="hover:cursor-pointer hover:text-red-700"
              color="red"
              onClick={onClose}
            />
          </div>

          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div>
              <h4 className="font-medium text-left">Billing Options</h4>
              <div className="mt-2 space-y-2">
                {billingOptions.length === 0 && <div className="text-sm text-gray-500">No billing options.</div>}
                {billingOptions.map((b) => (
                  <label
                    key={b.id}
                    className={`flex cursor-pointer items-center gap-3 rounded-md border p-3 ${selectedBillingId === b.id ? "border-blue-500 bg-blue-50" : "border-gray-200"
                      }`}
                  >
                    <input
                      type="radio"
                      name="billing"
                      checked={selectedBillingId === b.id}
                      onChange={() => {
                        setSelectedBillingId(b.id)
                        setAmount(b.bill as string)
                      }}
                      className="h-4 w-4"
                    />
                    <div>
                      <div className="font-medium">{billingLabel(b)}</div>
                      <div className="text-xs text-left text-gray-500">Billing Interval: {b.paymentInterval} months</div>
                    </div>
                  </label>
                ))}
              </div>

              <div className="mt-4 flex items-center gap-2">
                <input
                  id="autorenew"
                  type="checkbox"
                  checked={autoRenew}
                  onChange={(e) => setAutoRenew(e.target.checked)}
                />
                <label htmlFor="autorenew" className="text-sm">
                  Auto renew subscription
                </label>
              </div>

              {isFirstTimer && (
                <div className="flex-col flex gap-2">
                  <div className="mt-3 rounded border-l-4 border-blue-500 bg-blue-50 p-3 text-sm text-blue-800">
                    This is eligible for a free trial. The trial will apply automatically when confirming.
                  </div>
                  <div className="flex gap-2 mt-4">
                    <input
                      id="freeTrial"
                      type="checkbox"
                      checked={freeTrial}
                      onChange={(e) => setFreeTrial(e.target.checked)}
                    />
                    <label htmlFor="freeTrial" className="text-sm">
                      Enable Free trial
                    </label>
                  </div>
                </div>
              )}
            </div>

            <div>
              <h4 className="font-medium">Plan benefits</h4>
              <ul className="mt-2 space-y-2 text-left">
                <li>Items: <span className="text-primary font-semibold">{formatLimit(details?.itemLimit ?? null)}</span></li>
                <li>Sales limit: <span className="text-primary font-semibold">{formatLimit(details?.monthlySalesLimit ?? null)}</span></li>
                <li>Employees: <span className="text-primary font-semibold">{formatLimit(details?.employeeLimit ?? null)}</span></li>
                <li>Stores:  <span className="text-primary font-semibold">{formatLimit(details?.storeLimit ?? null)}</span></li>
                <li>Customers: <span className="text-primary font-semibold">{formatLimit(details?.customerLimit ?? null)}</span></li>
                <li>{details?.hasAnalytics ? "✅ Analytics" : "❌ Analytics"}</li>
                <li>{details?.hasNotifications ? "✅ Notifications" : "❌ Notifications"}</li>
                <li>{details?.hasDemandForecasting ? "✅ Demand Forecasting" : "❌ Demand Forecasting"}</li>
              </ul>

              <div className="mt-4">
                {freeTrial ? (
                  <button
                    type="button"
                    onClick={onConfirm}
                    disabled={submitting}
                    className="form-btn-mini w-full disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <div className="flex items-center justify-center gap-2">
                        <span className="loader"></span>
                        <span>Starting trial...</span>
                      </div>
                    ) : (
                      "Start Free Trial"
                    )}
                  </button>
                ) : (
                  <PaystackButton
                    email={user?.email as string}
                    amount={amount}
                    slug={`${user?.firstname} ${user?.lastname} - ${user?.Business.name}`}
                    onSuccess={onConfirm}
                  />
                )}
                <button
                  onClick={onClose}
                  className="mt-3 w-full rounded border px-4 py-2 text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal;
